import { Grid, makeStyles, Theme, useMediaQuery, useTheme } from "@material-ui/core";
import React, { FunctionComponent, useContext, useEffect, useMemo, useState } from "react";
import { Gender } from "../../api/smartmelts-api/graphql/generated";
import { DigitalTwin } from "../../components/digital-twin/digital-twin";
import { Categories } from "../../components/menu/categories";
import { ShopButton } from "../../components/shop-button/shop-button";
import { APP_BAR_HEIGHT } from "../../components/topbar/top-bar";
import { UserContext } from "../../components/user/user-context";
import { useWindowHeight } from "../../hooks/use-window-height";
import { MenuSubcategoryDictionary } from "../../models/categories";
import { MenuItemCategory } from "../../models/types/category-types";
import { AnswerContainer } from "./answer-container";
import { AnswerContext } from "./answer/context/answer-context";
import { CardContainer } from "./card-container";
import { NutritionContext } from "./context/nutrition-context";
import { ExplanationContextProvider } from "./explanation/context/explanation-context";

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    [theme.breakpoints.up("md")]: {
      flexWrap: "nowrap",
    },
  },
  mainSection: {
    position: "relative",
  },
  shopButtonContainer: {
    position: "absolute",
    width: "auto",
    top: 400,
    right: 0,
    "& :hover": {
      zIndex: 1101,
    },
    [theme.breakpoints.up("md")]: {
      position: "fixed",
      top: "initial",
      bottom: theme.spacing(4),
      right: theme.spacing(4),
      zIndex: 1101,
    },
  },
  cardContainer: {
    [theme.breakpoints.down("md")]: {
      marginBottom: theme.spacing(4),
    },
    [theme.breakpoints.up("md")]: {
      paddingRight: theme.spacing(10),
    },
  },
  modelContainer: {
    backgroundColor: `${theme.palette.background.default}`,
    boxShadow: `0px 0px 40px 18px ${theme.palette.background.default}`,
    [theme.breakpoints.down("md")]: {
      height: 500,
      overflowY: "visible",
    },
  },
  categoriesContainer: { zIndex: 2 },
  shopButton: {
    [theme.breakpoints.down("md")]: {},
  },
}));

interface INutritionPageContentProps {}

export const NutritionPageContent: FunctionComponent<INutritionPageContentProps> = (props) => {
  const classes = useStyles();
  const theme = useTheme();
  const windowHeight = useWindowHeight();
  const isMd = useMediaQuery(theme.breakpoints.up("md"));
  const canvasHeight = useMemo(() => (!isMd ? 500 : windowHeight - APP_BAR_HEIGHT), [windowHeight, isMd]);
  const { activeCategory, activeSubcategory, activeIndex, containerRef, scrollTop } = useContext(NutritionContext);
  const { activeAnswerIndex } = useContext(AnswerContext);
  const { userInformation } = useContext(UserContext);
  const [gender, setGender] = useState<Gender>(Gender.MALE);

  useEffect(() => {
    if (userInformation?.gender) {
      setGender(userInformation.gender);
    }
  }, [userInformation, setGender]);

  const showCategories = useMemo(() => activeCategory !== MenuItemCategory.USER, [activeCategory]);
  useEffect(() => {
    scrollTop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory, activeSubcategory, activeIndex, activeAnswerIndex]);

  return (
    <Grid container className={classes.container}>
      {isMd && (
        <Grid item xs={2} className={classes.categoriesContainer}>
          {showCategories && <Categories />}
        </Grid>
      )}
      <Grid item xs={12} md={2} className={classes.modelContainer}>
        <DigitalTwin height={canvasHeight} gender={gender} />
        <Grid item className={classes.shopButtonContainer}>
          <ShopButton />
        </Grid>
      </Grid>
      <Grid item container xs={12} md={8} className={classes.cardContainer}>
        <div ref={containerRef}></div>
        <ExplanationContextProvider>
          <NutritionPageContentSection />
        </ExplanationContextProvider>
      </Grid>
    </Grid>
  );
};

export const NutritionPageContentSection: React.VFC = () => {
  const classes = useStyles();
  const { activeAnswerIndex } = useContext(AnswerContext);
  const {
    activeSubcategory,
    changeActiveCategories,
    activeCategory,
    isFavourite,
    onUpdateUserFavourite,
    refetchIsFavourite,
  } = useContext(NutritionContext);

  const currentSubcategory = useMemo(() => MenuSubcategoryDictionary[activeSubcategory], [activeSubcategory]);

  return (
    <Grid container direction="column" className={classes.mainSection}>
      {currentSubcategory.isQuestionBased && (
        <AnswerContainer
          activeSubcategory={activeSubcategory}
          activeCategory={activeCategory}
          refetchIsFavourite={() => refetchIsFavourite(activeCategory, activeSubcategory, activeAnswerIndex + 1)}
          isFavourite={isFavourite}
          onUpdateUserFavourite={() => onUpdateUserFavourite(activeCategory, activeSubcategory, activeAnswerIndex + 1)}
        />
      )}
      {currentSubcategory && !currentSubcategory.isQuestionBased && (
        <CardContainer
          activeSubcategory={activeSubcategory}
          activeCategory={activeCategory}
          changeActiveCategories={changeActiveCategories}
          isFavourite={isFavourite}
          onUpdateUserFavourite={() => onUpdateUserFavourite(activeCategory, activeSubcategory)}
        />
      )}
    </Grid>
  );
};
